<script lang="ts" setup>
import type { FaqSectionsSchema } from '~/types/storyblok/faq-sections'
import { isFaqSection } from '~/types/storyblok/faq-section'

const props = defineProps<{ blok: FaqSectionsSchema }>()

const route = useRoute()
const isOverviewPage = computed(() => route.path.endsWith('/faq') || route.path.endsWith('/faq/'))
const textCards = computed(() => props.blok.sections
  .filter(isFaqSection)
  .map(s => ({
    ...s,
    cta: {
      title: s.cta_title,
      url: s.cta_link.cached_url,
    },
    highlights: (s.highlights || []).map(h => ({
      title: h.title,
      url: (h.link?.url || s.cta_link.cached_url) + (h.scrollTo ? `?scrollTo=${h.scrollTo}` : ''),
    })),
  })))
const tabs = computed(() =>
  props.blok.sections.map(s => ({
    title: s.title,
    url: isFaqSection(s) ? s.cta_link.cached_url : s.link?.cached_url || '',
  })),
)
const activeTab = computed(() => {
  const activeSection = route.params.faq_section?.toString()
  if (!activeSection)
    return undefined
  return tabs.value.findIndex(t => t.url.endsWith(`/${activeSection}`))
})
</script>

<template>
  <div v-editable="blok" class="faq-sections">
    <div v-if="isOverviewPage" class="faq-sections__overview">
      <TextCardsBlock :text-cards="textCards" />
    </div>

    <div v-else class="faq-sections__detail">
      <TabGroup :tabs="tabs" :active-tab="activeTab" />
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/typography/body';

.faq-sections {
  @include body1;
  padding-inline: var(--spacing-s);
  padding-block-start: var(--spacing-m);
  padding-block-end: 7.5rem;

  @media (min-width: $phablet) {
    padding-inline: var(--spacing-m);
  }

  &__detail {
    display: grid;
    max-width: 1056px;
    gap: var(--spacing-l);

    @media (min-width: $phablet) {
      grid-template-columns: 1fr 3fr;
      gap: var(--spacing-m);
    }
  }
}
</style>
